/**
 * The gathering verb (M3-03).
 *
 * Design pillar 6, stated as a rule: **not everyone wants to face the boar.**
 * Someone has to keep the healer's satchel full, and that player has to matter
 * as much as the one with the spear. So forage is built like the combat verbs
 * rather than like a resource node — it is channelled, it commits you in the
 * open, the log broadcasts it, and a blow ends it.
 *
 * Three small mechanics carry the whole ticket, and each is deliberately
 * invisible:
 *
 * - **Familiarity.** One capped counter per character, no UI, no level-up
 *   line. You get better at finding things and one day notice that you are.
 *   The design spec's skill-by-use in miniature.
 * - **Identification.** The first successful pick teaches you the plant, and
 *   after that it examines as itself. Before, it is "a white-flowered herb".
 *   The knowledge is on the *character* and survives their death — see the
 *   note on `Lore` in `item.ts`.
 * - **Scarcity.** A patch has a few picks in it and then it is bare for days.
 *   Nobody is told a number; the meadow simply stops giving.
 */
import { capabilityFault } from './affordance.js';
import { parseForage } from './actions.js';
import { canForage, floraKey, regrow, standingOf, take } from './flora.js';
import { isChannelling, isCommitted, isHelpless } from './fighter.js';
import { addItem, createInventory, createLore, familiarityBonus, knows, learn, MAX_FORAGE_FAMILIARITY, nameFor, } from './item.js';
import { TICKS_PER_SECOND } from './time.js';
/**
 * How long a pick takes.
 *
 * Six seconds — shorter than robbing a body (ten) and longer than a staunch
 * (five). The ordering is the statement: taking a man's mail off him should be
 * the most exposed thing you can choose to do standing still, and pulling moss
 * should still be long enough that doing it on the wrong side of the river is
 * a decision.
 */
export const FORAGE_TICKS = TICKS_PER_SECOND * 6;
/**
 * Why this actor may not forage here, or `null`.
 *
 * The `attackFault`/`helplessFault` pattern for the third time: one predicate
 * that both the intent check and the channel's completion consult, so "may I"
 * and "may I still" cannot drift. Six seconds is long enough for somebody else
 * to have taken the last of it.
 */
export function forageFault(world, catalogue, actor, tick) {
    const combatant = world.combat[actor.id];
    if (combatant) {
        if (isHelpless(catalogue, combatant, tick))
            return 'you cannot';
        if (isCommitted(combatant, tick))
            return 'you are in the middle of something';
        if (isChannelling(combatant, tick))
            return 'you are already busy';
        // What gathering asks of the hands (M3-04), from the affordance registry.
        const unable = capabilityFault(catalogue, combatant, 'forage');
        if (unable)
            return unable;
    }
    const patch = world.flora[floraKey(actor.x, actor.y)];
    if (!patch)
        return 'there is nothing to gather here';
    const def = catalogue.flora?.(patch.speciesId) ?? null;
    if (!def)
        return 'there is nothing to gather here';
    if (!canForage(patch)) {
        // Named, not numbered. "Picked bare" is a thing you can see; "0/3" is not.
        return `the ${def.name.replace(/^an? /, '')} here has been picked bare`;
    }
    return null;
}
/* -------------------------------------------------------------------------- *
 * The intent
 * -------------------------------------------------------------------------- */
export const forageHandler = {
    type: 'forage',
    parse: parseForage,
    check(_intent, ctx) {
        const actor = ctx.world.actors[_intent.actorId];
        if (!actor)
            return 'you are not in the world';
        return forageFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return;
        const combatant = ctx.world.combat[actor.id];
        if (!combatant)
            return;
        combatant.channel = {
            verb: 'forage',
            // Ground, not a body. See `Channel.targetId`.
            targetId: null,
            startedAt: ctx.tick,
            completesAt: ctx.tick + FORAGE_TICKS,
            fromX: actor.x,
            fromY: actor.y,
            detail: null,
        };
        const patch = ctx.world.flora[floraKey(actor.x, actor.y)];
        const def = patch ? (ctx.catalogue.flora?.(patch.speciesId) ?? null) : null;
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['forage', 'begin', patch?.speciesId ?? ''],
            text: def ? `${actor.name} kneels to gather ${bare(def.name)}.` : undefined,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
/** "a stand of yarrow" → "the stand of yarrow", for mid-sentence use. */
function bare(name) {
    return name.replace(/^an? /, 'the ');
}
/* -------------------------------------------------------------------------- *
 * Completion
 * -------------------------------------------------------------------------- */
/**
 * Finish a pick.
 *
 * Called by `interactSystem`, which owns the channel clock for every verb.
 * The fault is re-run first: the patch may have been emptied by somebody
 * kneeling on the other side of it while this channel ran.
 */
export function finishForage(ctx, actor) {
    const fault = forageFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    if (fault !== null) {
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['forage', 'failed', fault],
            text: `${actor.name} comes away with nothing.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
        return;
    }
    const patch = ctx.world.flora[floraKey(actor.x, actor.y)];
    const def = ctx.catalogue.flora?.(patch.speciesId);
    const item = def ? ctx.catalogue.item?.(def.itemId) : null;
    if (!def || !item)
        return;
    const lore = (ctx.world.lore[actor.id] ??= createLore());
    const inventory = (ctx.world.inventories[actor.id] ??= createInventory());
    // Familiarity buys at most one extra, and the fraction is spent as a chance
    // rather than rounded — otherwise the counter would do nothing at all until
    // it crossed halfway and then jump.
    const bonus = familiarityBonus(lore);
    const extra = Math.floor(bonus) + (ctx.rng('forage').chance(bonus % 1) ? 1 : 0);
    const yielded = def.yield + extra;
    addItem(inventory, item.id, yielded, ctx.tick, item.stackable, undefined, item.shelfLife !== null);
    take(patch, def, ctx.tick);
    // Practice, capped. Counted per pick rather than per item, so stripping a
    // generous patch does not teach you more than working a poor one.
    if (lore.forage < MAX_FORAGE_FAMILIARITY)
        lore.forage += 1;
    // The moment the plant acquires a name. Once only, and it is the one piece of
    // progression in the whole ticket that the player is actually told about —
    // because being told is the reward.
    const firstTime = learn(lore, item.id);
    ctx.emit({
        type: 'interacted',
        actors: [actor.id],
        refs: ['forage', 'done', item.id, String(yielded), standingOf(patch, def)],
        text: firstTime
            ? `${actor.name} gathers ${item.name}, and knows it now for what it is.`
            : `${actor.name} gathers ${nameFor(item, lore)}.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/* -------------------------------------------------------------------------- *
 * Regrowth
 * -------------------------------------------------------------------------- */
/**
 * Bring the valley back, on the hour.
 *
 * A sorted sweep of every patch, like `reconcileDen`'s: at a few thousand
 * patches and once a game hour this is nothing, and iterating in key order
 * means the tick cannot depend on insertion order.
 */
export const floraSystem = {
    name: 'flora',
    // The engine's own hourly phase, rather than a modulo inside a `sim` system:
    // the scheduler already knows how to run something once a game hour, and a
    // system that filtered ticks itself would be a second answer to a question
    // that has one.
    phase: 'hour',
    run(ctx) {
        for (const key of Object.keys(ctx.world.flora).sort()) {
            const patch = ctx.world.flora[key];
            if (!patch)
                continue;
            const def = ctx.catalogue.flora?.(patch.speciesId);
            if (!def)
                continue;
            regrow(patch, def, ctx.tick);
        }
    },
};
/* -------------------------------------------------------------------------- *
 * Reading the ground
 * -------------------------------------------------------------------------- */
/**
 * What a patch examines as, given who is looking.
 *
 * The identification rule, applied to the plant rather than to the item: an
 * unknown yarrow stand reads as a stand of white-flowered herbs, and a known
 * one reads as yarrow. Standing is always visible, because whether a meadow
 * has been picked over is a thing anybody can see.
 */
export function describeFlora(catalogue, patch, lore) {
    const def = catalogue.flora?.(patch.speciesId);
    if (!def)
        return 'Something grows here.';
    const item = catalogue.item?.(def.itemId);
    const known = item ? knows(lore, item.id) : false;
    const head = known && item ? `${capitalize(def.name)} — ${item.name}.` : `${capitalize(def.name)}.`;
    switch (standingOf(patch, def)) {
        case 'thick':
            return `${head} There is plenty of it.`;
        case 'thin':
            return `${head} Somebody has been at it, but there is more.`;
        default:
            return `${head} It has been picked bare; it will be a while coming back.`;
    }
}
function capitalize(text) {
    return text.slice(0, 1).toUpperCase() + text.slice(1);
}
export const FORAGE_INTENTS = [forageHandler];
//# sourceMappingURL=forage.js.map